import axios from ".";

export type Organization = {
  id: string;
  name: string;
  created_at?: string;
  mode_allow_create: boolean;
  mode_allow_translate: boolean;
  mode_allow_ai_generation: boolean;
  mode_allow_translate_ai: boolean;
  disabled: boolean;
};

export type CreateOrganizationData = {
  name: string;
  mode_allow_create: boolean;
  mode_allow_translate: boolean;
  mode_allow_ai_generation: boolean;
  mode_allow_translate_ai: boolean;
};

export type EditOrganizationData = {
  name: string;
  mode_allow_create: boolean;
  mode_allow_translate: boolean;
  mode_allow_ai_generation: boolean;
  mode_allow_translate_ai: boolean;
};

export type UsageStats = {
  monthly: Array<{
    month: string;
    total: number;
    by_language: Record<string, number>;
  }>;
  grand_total: number;
};

export type GlobalUsageStats = {
  monthly: Array<{
    month: string;
    total: number;
    organizations: Array<{
      id: string;
      name: string;
      total: number;
      by_language: Record<string, number>;
    }>;
  }>;
  grand_total: number;
};

export type AIGeneratorType =
  | "TITLE_GENERATION"
  | "COLUMN_DESCRIPTION_GENERATION"
  | "SHORT_DESCRIPTION_GENERATION"
  | "LONG_DESCRIPTION_GENERATION"
  | "REGULAR_DESCRIPTION_GENERATION";

export type AIGeneratorUsageByModel = {
  name: string;
  cost_usd: number;
  generations: number;
};

export type AIGeneratorUsageByGenerator = {
  cost_usd: number;
  generations: number;
  models: AIGeneratorUsageByModel[];
};

export type AIGenerationUsageStats = {
  monthly: Array<{
    month: string;
    total_cost_usd: number;
    total_generations: number;
    by_generator: Partial<Record<AIGeneratorType, AIGeneratorUsageByGenerator>>;
  }>;
  grand_total_cost_usd: number;
  grand_total_generations: number;
};

export type GlobalAIGenerationUsageStats = {
  monthly: Array<{
    month: string;
    total_cost_usd: number;
    total_generations: number;
    organizations: Array<{
      id: string;
      name: string;
      total_cost_usd: number;
      total_generations: number;
      by_generator: Partial<Record<AIGeneratorType, AIGeneratorUsageByGenerator>>;
    }>;
  }>;
  grand_total_cost_usd: number;
  grand_total_generations: number;
};

export const getOrganizations = async (name?: string) => {
  const params = name ? { name } : {};
  return axios.get(`/organization`, { params });
};

export const getOrganization = async (organizationId: string) => {
  return axios.get(`/organization/${organizationId}`);
};

export const createOrganization = async (data: CreateOrganizationData) => {
  return axios.post(`/organization`, data);
};

export const editOrganization = async (
  organizationId: string,
  data: EditOrganizationData
) => {
  return axios.patch(`/organization/${organizationId}`, data);
};

export const deleteOrganization = async (organizationId: string) => {
  return axios.delete(`/organization/${organizationId}`);
};

export const disableOrganization = async (organizationId: string) => {
  return axios.post(`/organization/${organizationId}/disable`);
};

export const enableOrganization = async (organizationId: string) => {
  return axios.post(`/organization/${organizationId}/enable`);
};

export const getOrganizationUsers = async (organizationId: string) => {
  return axios.get(`/organization/${organizationId}/users`);
};

export const getOrganizationBrands = async (organizationId: string) => {
  return axios.get(`/organization/${organizationId}/brands`);
};

export const associateBrandToOrganization = async (
  organizationId: string | null,
  brandId: string
) => {
  // Fetch the brand first to get all required fields
  const brandResponse = await axios.get(`/brand/${brandId}`);
  const brand = brandResponse.data.data;

  // Send all required fields with updated organization_id
  return axios.patch(`/brand/${brandId}`, {
    ...brand,
    organization_id: organizationId,
  });
};

export const getOrganizationUsage = async (organizationId: string) => {
  return axios.get<{ data: UsageStats }>(`/organization/${organizationId}/usage`);
};

export const getGlobalUsage = async () => {
  return axios.get<{ data: GlobalUsageStats }>(`/organization/usage/global`);
};

export const getOrganizationAIUsage = async (organizationId: string) => {
  return axios.get<{ data: AIGenerationUsageStats }>(
    `/organization/${organizationId}/ai-usage`
  );
};

export const getGlobalAIUsage = async () => {
  return axios.get<{ data: GlobalAIGenerationUsageStats }>(
    `/organization/ai-usage/global`
  );
};
